import { Cart, CartLineItem, UpdateCartInput } from '../types'
import { calculateCartTotals } from './calculate-cart-totals'
import { getOptimisticItem } from './optimistic-add-line-items'

/**
 * Optimistically updates a cart with new item quantities and other changes.
 *
 * This function applies quantity updates to cart items and recalculates line prices,
 * discounts, and totals based on the new quantities. It performs an optimistic update
 * without waiting for server confirmation.
 *
 * @param input - The update input containing item quantity changes and other cart modifications
 * @param initialCart - The current cart state to be updated
 * @returns A new cart object with updated items and recalculated totals
 *
 * @remarks
 * - Line prices are recalculated based on item price multiplied by new quantity
 * - Total discounts are scaled proportionally with quantity changes
 * - Item grams remain unchanged as they don't scale with quantity
 * - Uses `calculateCartTotals` to compute final cart totals
 */
export function optimisticUpdateCart(input: UpdateCartInput, initialCart: Cart): Cart {
  const { updates, ...rest } = input

  const newItems: CartLineItem[] = []
  const idsInCart = new Set<number>()

  for (const item of initialCart.items) {
    idsInCart.add(item.id)
    // If the item is being updated, apply the changes
    const newQuantity = updates?.[item.id.toString()]
    if (newQuantity !== undefined) {
      if (newQuantity === 0) {
        // If quantity is set to 0, we remove the item
        continue
      }
      newItems.push({
        ...item,
        quantity: newQuantity,
        original_line_price: item.original_price * newQuantity,
        final_line_price: item.price * newQuantity,
        total_discount: item.total_discount * newQuantity,
        grams: item.grams // Assuming grams doesn't change with quantity
      })
    } else {
      newItems.push(item)
    }
  }

  for (const id in updates) {
    if (!idsInCart.has(Number(id))) {
      const quantity = updates[id]
      const optimisticItem = getOptimisticItem({ id: Number(id), quantity })
      newItems.unshift(optimisticItem)
    }
  }

  return calculateCartTotals(newItems, {
    ...initialCart,
    ...rest
  })
}
